import { cloneDeep, isNumber, pull, pullAll } from 'lodash';
import { v4 as uuid } from 'uuid';
import { getGroupExtension, mergeMaterialIds, trackConfig } from '@/framework';
import { getDefaultImageLayerData } from '@/layers/image-layer/constants/default-image-data';
import { Layer } from '@/layers/layer';
import type { GroupAlign } from '@/layers/layer/group-align';
import { base64Kit } from '@/utils/base64-kit';
import { getImgLayerProps } from '@/utils/layer-kit';
import { traverseTreePreOrder } from '@/utils/tree-kit';
import { LayerType, PluginType, type IExportPlugin, type IGroupLayerProps, type ILayerProps } from '@/version-types';
import groupLayerBehavior from './behavior';
import type { EdgelessGroupLayer } from './edgeless-layer';
import { GroupHistory } from './group-history';
import { HistoryType } from './type';

export { GroupLayerHotkeyType } from './type';
export * from './plugins';

export class GroupLayer extends Layer {
    align: GroupAlign;

    constructor(identity: string) {
        super(identity);
        this.align = this.groupAlign;
    }

    /**
     * 检查是否是图层组
     *
     * @param layer - 图层属性
     */
    checkIsGroup(layer: ILayerProps | null) {
        if (!layer) return false;
        return this.framework.service.flatLayer.checkIsGroup(layer);
    }

    /**
     * 检查是否是临时组
     *
     * @param layer - 图层属性
     */
    checkIsExtraGroup(layer: ILayerProps | null) {
        if (!layer) return false;
        return this.framework.service.flatLayer.checkIsExtraGroup(layer);
    }

    /**
     * 检查是否是正式组
     *
     * @param layer - 图层属性
     */
    checkIsFormalGroup(layer: ILayerProps | null) {
        if (!layer) return false;
        return this.framework.service.flatLayer.checkIsFormalGroup(layer);
    }

    /**
     * 检查是否可以拆分
     *
     * @param layer - 图层属性
     */
    checkBroken(layer: ILayerProps | null) {
        if (!layer) return false;
        return this.framework.service.flatLayer.checkBrokenGroup(layer);
    }

    /**
     * 检查是否是拓展图层
     *
     * @param layer - 图层属性
     */
    checkIsGroupExtension(layer: ILayerProps | null) {
        if (!layer) return false;
        return this.framework.service.flatLayer.checkIsGroupExtension(layer);
    }

    /**
     * 获取拓展图层
     *
     * @param layer - 图层属性
     */
    getExtension<T>(layer: ILayerProps | null) {
        if (!layer) return null;
        return getGroupExtension<T>(layer);
    }

    /**
     * 成组
     *
     * @param layerId - 图层 ID
     */
    pack(layerId: string) {
        const { flatLayer, frame } = this.framework.service;
        const layer = flatLayer.getLayer(layerId, true);

        if (!flatLayer.checkIsExtraGroup(layer)) return;
        if (this.framework.service.agentLayout.checkArranging(layer)) return;

        const originIndex = frame.getLayerFrameId(layerId);
        if (!isNumber(originIndex)) return;

        const info = frame.getFrameIdAndLayerIds(originIndex);
        const originIds = info.layerIds;

        const childIndexes: number[] = [];
        const childIds: string[] = [];
        layer.children.forEach((v) => {
            const id = v.id;
            if (originIds.indexOf(id) !== -1) {
                childIndexes.push(originIds.indexOf(id));
                childIds.push(id);
            }
        });

        const maxChildNextLayerId = originIds[Math.max(...childIndexes) + 1];
        pullAll(originIds, childIds);
        if (maxChildNextLayerId !== layerId) {
            pull(originIds, layerId);
            const insertIndex = originIds.indexOf(maxChildNextLayerId);
            originIds.splice(insertIndex, 0, layerId);
        }
        (layer.data as IGroupLayerProps).isExtraGroup = false;
        frame.addOrUpdateLayer(layer, originIndex, true);
        frame.emitSortLayer(originIndex);
        this.framework.service.history.write({
            type: HistoryType.PACK,
            data: {
                item: {
                    childIndexes,
                    childIds,
                    layer: cloneDeep(layer),
                    layerIndex: originIds.indexOf(layer.id),
                },
            },
            originIndex,
            trackFunc: trackConfig[LayerType.GROUP_LAYER]?.group,
            needSyncRenderIds: true,
        });
        frame.setActiveLayer(layerId);
    }

    /**
     * 解组
     *
     * @param layerId - 图层 ID
     */
    unpack(layerId: string) {
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (
            !this.framework.service.flatLayer.checkIsFormalGroup(layer) ||
            !this.framework.service.flatLayer.checkBrokenGroup(layer)
        )
            return;

        if (this.framework.service.agentLayout.checkArranging(layer)) return;

        const originIndex = this.framework.service.frame.getLayerFrameId(layerId);
        if (!isNumber(originIndex)) return;

        const info = this.framework.service.frame.getFrameIdAndLayerIds(originIndex);
        const originIds = info.layerIds;

        const layerIndex = originIds.indexOf(layerId);
        if (layerIndex === -1) return;

        const childIds: string[] = (layer.children || []).map((v) => v.id);
        if (childIds.length === 0) return;

        pull(originIds, layerId);
        originIds.splice(layerIndex, 0, ...childIds);
        originIds.push(layerId);

        const oldLayer = cloneDeep(layer);
        (oldLayer.data as IGroupLayerProps).isExtraGroup = true;
        (layer.data as IGroupLayerProps).isExtraGroup = true;
        layer.lock = false;
        this.framework.service.frame.addOrUpdateLayer(layer, originIndex, true);
        this.framework.service.frame.emitSortLayer(originIndex);
        this.framework.service.history.write({
            type: HistoryType.UNPACK,
            data: {
                item: {
                    layer: oldLayer,
                    layerIndex: originIds.indexOf(layer.id),
                },
            },
            originIndex,
            trackFunc: trackConfig[LayerType.GROUP_LAYER]?.unGroup,
            needSyncRenderIds: true,
        });
        this.framework.service.frame.setActiveLayer(layerId);
    }
    /**
     * 合成图片
     *
     * @param layerId - 图层 ID
     */
    async merge(layerId: string) {
        const { flatLayer, frame } = this.framework.service;
        let layer = flatLayer.getLayer(layerId, true);
        if (!layer || layer.lock) return;
        if (this.framework.service.agentLayout.checkArranging(layer)) return;
        if (!(await this.exitEditingBeforeMerge(layer))) return;

        layer = flatLayer.getLayer(layerId, true);
        if (!layer || layer.lock) return;
        if (this.framework.service.agentLayout.checkArranging(layer)) return;

        const mergeResult = await this.resolveMergeResult(layer, 'png');
        if (!mergeResult) return;
        const { imageUrl, renderBox, naturalWidth, naturalHeight } = mergeResult;
        const materialIds: Record<string, string[]> = {};
        traverseTreePreOrder(layer, (item: ILayerProps) => {
            mergeMaterialIds(materialIds, item.materialIds);
        });
        const imageLayerData = getDefaultImageLayerData(
            imageUrl,
            renderBox.width,
            renderBox.height,
            renderBox.left,
            renderBox.top,
            materialIds,
            naturalWidth,
            naturalHeight,
        );
        const imageLayer = getImgLayerProps(imageLayerData);
        const latestLayer = flatLayer.getLayer(layerId);
        if (!flatLayer.checkLayerEqual(layer, latestLayer)) {
            console.log('ot 图层不一致，丢弃');
            return;
        }
        const originIndex = layer.__originIndex;
        const delData = frame.delLayer(layerId);
        const insertData = frame.addOrUpdateLayer(imageLayer, originIndex);
        frame.setActiveLayer(insertData.id);
        if (delData) {
            // const url = await base64Kit.getRemoteUrlAsync((insertData.data as IImageLayerProps).url);
            // (insertData.data as IImageLayerProps).url = url;
            this.framework.service.history.write({
                type: HistoryType.MERGE,
                data: {
                    delData,
                    insertData,
                },
                originIndex,
                needSyncRenderIds: true,
                // trackFunc: trackFunc || getTrackFunc(layer, 'merge'),
            });
        }
    }

    /**
     * 获取合并的图层数据
     * @param layer - 图层属性
     * @param type - 可选；图片格式；默认值为 `png`
     * @returns 合并后的图片资源 code，或无法生成时返回 null。
     */
    async getMergeResult(layer: ILayerProps, type = 'png') {
        if (!(await this.exitEditingBeforeMerge(layer))) return null;

        const latestLayer = this.framework.service.flatLayer.getLayer(layer.id, true);
        if (!latestLayer) return null;
        const result = await this.resolveMergeResult(latestLayer, type);
        return result?.imageUrl ?? null;
    }

    /**
     * 合成前通过图层 Behavior 提交并退出组内编辑态。
     *
     * @param layer - 即将合成的组图层
     * @returns 所有图层无需退出或成功退出时返回 true；任一图层无法退出时返回 false
     */
    private async exitEditingBeforeMerge(layer: ILayerProps) {
        const layers: ILayerProps[] = [];
        traverseTreePreOrder(layer, (item: ILayerProps) => layers.push(item));

        for (const item of layers) {
            const result = await this.framework.service.behavior.get(item)?.exitEditing?.(item);
            if (result === false) return false;
        }
        return true;
    }

    private async resolveMergeResult(layer: ILayerProps, type: string) {
        const object = this.framework.service.edgelessCanvas.getByLayerId(layer.id) as EdgelessGroupLayer | null;
        if (!object) return null;
        const result = await object.getResult({ scale: 2 });
        if (!result.canvas) return null;
        const { canvas, renderBox } = result;

        const mimeType = type === 'jpg' || type === 'jpeg' ? 'image/jpeg' : 'image/png';
        return {
            imageUrl: base64Kit.getCode(canvas.toDataURL(mimeType), uuid()),
            renderBox,
            naturalWidth: canvas.width,
            naturalHeight: canvas.height,
        };
    }

    /**
     * 创建临时组并激活
     *
     * @param childIds - 子图层 ID 数组
     */
    createGroupAndActive(childIds: string[]) {
        const finalChildIds: string[] = [];
        for (let i = 0; i < childIds.length; i++) {
            const id = childIds[i];
            const layer = this.framework.service.flatLayer.getLayer(id);
            if (!this.framework.service.behavior.get(layer)?.checkDisableInGroup?.(layer)) {
                finalChildIds.push(id);
            }
        }
        if (finalChildIds.length <= 1) return;
        const originIndex =
            this.framework.service.flatLayer.getLayer(finalChildIds[0])?.__originIndex ??
            this.framework.service.frame.getActiveFrameId();
        const groupLayer = this.framework.service.frame.tryCreateGroup(originIndex, uuid(), new Set(finalChildIds));
        if (!groupLayer) return;
        this.framework.service.frame.addOrUpdateLayer(groupLayer, originIndex, true);
        this.framework.service.frame.emitSortLayer(originIndex);
        this.framework.service.frame.setActiveLayer(groupLayer.id);
        this.framework.service.frame.delHoverId();
    }
}

/**
 * 组图层插件
 */
export const groupLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.GROUP_LAYER,
    createInstance({ identity }) {
        return [{ key: 'GroupLayer', value: new GroupLayer(identity) }];
    },
    createBehavior() {
        return groupLayerBehavior;
    },
    createHistory({ identity }) {
        return [new GroupHistory(identity)];
    },
};
